import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import {
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { styled } from "@mui/system";
import styles from "./DcSupplyInsights.module.css";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CustomPagination from "Samarth Module/components/CustomPagination/CustomPagination";
import ExpandRight from "../../../../assets/images/expandRight.svg";

const StyledTableCell = styled(TableCell)(() => ({
  "&.MuiTableCell-head": {
    fontFamily: "Unilever Shilling",
    backgroundColor: "#3447d4",
    color: "#fff",
    textAlign: "center",
    padding: "8px 10px",
    fontSize: "12px",
    fontWeight: 400,
    border: "1px solid #EAECF0",
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    lineHeight: 1.2,
    boxSizing: "border-box",
    verticalAlign: "middle",
  },
  "&.MuiTableCell-body": {
    fontFamily: "Unilever Shilling",
    fontSize: 12,
    fontWeight: 400,
    padding: "5.5px 8px",
    color: "#2B2B2B",
    border: "1px solid #EAECF0",
    boxSizing: "border-box",
    verticalAlign: "middle",
  },
}));

const StyledTableRow = styled(TableRow)({});

const COLLAPSED_COL_WIDTH = 140;
const EXPANDED_COL_WIDTH = 250;

const SHIMMER_ROWS = ["row-1", "row-2", "row-3", "row-4", "row-5", "row-6"];

// Extracted helper components
const TableRowShimmer = React.memo(({ columns, subColumnsMap }) => {
  const totalCells = columns.reduce((acc, col) => {
    const subs = subColumnsMap?.[col.label] || [];
    return acc + Math.max(subs.length, 1);
  }, 0);

  return (
    <TableRow>
      {Array.from({ length: totalCells }, (_, cellNumber) => {
        const shimmerKey = `shimmer-cell-${cellNumber + 1}`;

        return (
          <StyledTableCell key={shimmerKey}>
            <div className={styles.tableCellShimmer} data-testid="table-cell-shimmer" />
          </StyledTableCell>
        );
      })}
    </TableRow>
  );
});

TableRowShimmer.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
    }),
  ).isRequired,
  subColumnsMap: PropTypes.object,
};

TableRowShimmer.displayName = "TableRowShimmer";

const HeaderShimmer = React.memo(({ columns }) => (
  <TableRow>
    {columns.map((column) => {
      const shimmerHeaderKey = `header-shimmer-${column?.label ?? "unknown"}`;

      return (
        <StyledTableCell key={shimmerHeaderKey}>
          <div className={styles.tableHeaderShimmer} data-testid="table-header-shimmer" />
        </StyledTableCell>
      );
    })}
  </TableRow>
));

HeaderShimmer.propTypes = {
  columns: PropTypes.array.isRequired,
};

HeaderShimmer.displayName = "HeaderShimmer";

// Helper function for keyboard accessibility
const handleKeyboardAction = (event, callback) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    callback(event);
  }
};

// Helper functions for nested ternary refactoring
const getSortDirection = (isActive, currentDirection) => {
  if (!isActive) return "ASC";
  return currentDirection === "ASC" ? "DESC" : "ASC";
};

const getSortIconColor = (colIdx) => {
  return colIdx % 2 === 0 ? "rgb(11, 31, 68)" : "rgb(255, 255, 255)";
};

const getBackgroundColor = (colIdx) => {
  return colIdx % 2 === 0 ? "rgb(194, 224, 255)" : "rgb(93, 110, 215)";
};

const getTextColor = (colIdx) => {
  return colIdx % 2 === 0 ? "rgb(11, 31, 68)" : "rgb(255, 255, 255)";
};

// Helper functions for cell rendering
const getCellStyles = (idx, additionalStyles = {}) => ({
  backgroundColor: idx === 0 ? "#F6F7F0" : "transparent",
  fontWeight: idx === 0 ? "700 !important" : "400 !important",
  ...additionalStyles,
});

const getNumberTitle = (rawVal) => {
  return typeof rawVal === "number" ? rawVal : undefined;
};

// Helper function to determine if column is a percentage section
const isPercentageSectionColumn = (columnLabel, percentageColumns) => {
  return percentageColumns.some(
    (col) =>
      col.toLowerCase().replaceAll(/\s+/g, "") ===
      columnLabel.toLowerCase().replaceAll(/\s+/g, ""),
  );
};

// Helper component for sort icons
const SortIcon = React.memo(({ isActive, sortDirection, size = 18, color }) => {
  if (isActive) {
    if (sortDirection === "ASC") {
      return <ArrowDropUpIcon sx={{ fontSize: size, color }} />;
    }
    return <ArrowDropDownIcon sx={{ fontSize: size, color }} />;
  }

  return (
    <span
      style={{
        display: "flex",
        flexDirection: "column",
        opacity: 0.5,
      }}
    >
      <ArrowDropUpIcon
        sx={{
          fontSize: size,
          marginBottom: "-8px",
          color,
        }}
      />
      <ArrowDropDownIcon sx={{ fontSize: size, color }} />
    </span>
  );
});

SortIcon.propTypes = {
  isActive: PropTypes.bool.isRequired,
  sortDirection: PropTypes.string.isRequired,
  size: PropTypes.number,
  color: PropTypes.string,
};

SortIcon.displayName = "SortIcon";

// Helper component for expand icon
const ExpandIcon = React.memo(({ isExpanded, onClick }) => (
  <button
    onClick={onClick}
    onKeyDown={(e) => handleKeyboardAction(e, onClick)}
    style={{
      display: "inline-flex",
      alignItems: "center",
      cursor: "pointer",
      flexShrink: 0,
      background: "none",
      border: "none",
      padding: 0,
    }}
    title="Expand/Collapse"
    type="button"
  >
    <img
      src={ExpandRight}
      alt="expand"
      style={{
        width: 20,
        height: 20,
        transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
        transition: "transform 0.2s ease",
      }}
    />
  </button>
));

ExpandIcon.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

ExpandIcon.displayName = "ExpandIcon";

// Sub-column helpers
const SUB_COLUMN_PREFIX = "Sub_";

const normalizeColumnLabel = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replaceAll(/\s+/g, "")
    .replaceAll(/&amp;|&/g, "");

const isSubColumn = (label) =>
  typeof label === "string" && label.startsWith(SUB_COLUMN_PREFIX);

const getSubColumnMeta = (label) => {
  if (!isSubColumn(label)) {
    return null;
  }

  const rawLabel = label.slice(SUB_COLUMN_PREFIX.length);
  const separatorIndex = rawLabel.lastIndexOf("_");

  if (separatorIndex < 0) {
    return null;
  }

  return {
    parentLabel: rawLabel.slice(0, separatorIndex).trim(),
    childLabel: rawLabel.slice(separatorIndex + 1).trim(),
  };
};

const isSubColumnOfParent = (column, parentLabel) => {
  const meta = getSubColumnMeta(column?.label);

  if (!meta) {
    return false;
  }

  return (
    normalizeColumnLabel(meta.parentLabel) ===
    normalizeColumnLabel(parentLabel)
  );
};

const DcOverviewtable = ({
  data,
  columns,
  paginationData,
  sortingData,
  handlePageChange,
  isLoading = false,
  scrollableColumnsArray = [],
  fixedColumnName,
  totalColumnName,
  tableType,
  isInitialLoading,
}) => {
  const [tableData, setTableData] = useState(data || []);
  const [tableColumns, setTableColumns] = useState(columns || []);
  const [sortColumn, setSortColumn] = useState(sortingData?.sortBy || null);
  const [sortDirection, setSortDirection] = useState(
    sortingData?.sortOrder || "ASC",
  );
  const [fieldSection, setFieldSection] = useState(
    sortingData?.fieldsection || "",
  );
  const [page, setPage] = useState(paginationData?.page || 1);
  const [rowsPerPage, setRowsPerPage] = useState(
    paginationData?.rowsPerPage || 15,
  );
  const [expandedColumns, setExpandedColumns] = useState({});
  const [isSortingLoading, setIsSortingLoading] = useState(false);

  const fixedHeadRef = useRef(null);
  const scrollHeadRef = useRef(null);
  const fixedRowRefs = useRef([]);
  const scrollRowRefs = useRef([]);

  const NON_FORMATTED_COLUMNS = ["Basepack", "Bacepack desc", "Material"];
  const PERCENTAGE_SECTION_COLUMNS = ["NR Loss Component %"];

  const formatToCappedUnit = (value) => {
    if (value === null || value === undefined) return "-";
    if (value < 1000) return `${value}`;
    if (value < 100000) return `${(value / 1000).toFixed(1)} K`;
    if (value < 10000000) return `${(value / 100000).toFixed(1)} L`;
    return `${(value / 10000000).toFixed(1)} Cr`;
  };

  const formatValue = (value, columnLabel = "") => {
    if (value === null || value === undefined) return "-";

    const normalized = columnLabel.toLowerCase().replaceAll(/\s+/g, "");
    const isNonFormatted = NON_FORMATTED_COLUMNS.some(
      (col) => col.toLowerCase().replaceAll(/\s+/g, "") === normalized,
    );

    if (isNonFormatted) {
      return value;
    }

    const isPercentageSection = PERCENTAGE_SECTION_COLUMNS.some(
      (col) => col.toLowerCase().replaceAll(/\s+/g, "") === normalized,
    );

    if (isPercentageSection) {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        return `${numericValue.toFixed(1)}%`;
      }
      return value;
    }

    if (typeof value === "number") {
      if (value >= 10000) {
        return formatToCappedUnit(value);
      }
      return value.toLocaleString("en-IN");
    }

    return value;
  };

  const normalizeLabel = (label = "") => label.trim().toLowerCase();

  const isTruncatableColumn = (label) => {
    return ["basepack desc", "format", "action recommended"].includes(
      normalizeLabel(label),
    );
  };

  const getExpandKey = (sectionLabel, subKey = "") => {
    return subKey ? `${sectionLabel}__${subKey}` : sectionLabel;
  };

  const isExpanded = (sectionLabel, subKey = "") => {
    return !!expandedColumns[getExpandKey(sectionLabel, subKey)];
  };

  const getColumnWidth = (sectionLabel, subKey = "") => {
    return isExpanded(sectionLabel, subKey)
      ? `${EXPANDED_COL_WIDTH}px`
      : `${COLLAPSED_COL_WIDTH}px`;
  };

  const getExpandableCellStyles = (sectionLabel, subKey = "") => {
    const expanded = isExpanded(sectionLabel, subKey);
    const width = getColumnWidth(sectionLabel, subKey);

    return {
      minWidth: width,
      maxWidth: width,
      width,
      whiteSpace: expanded ? "normal" : "nowrap",
      overflow: expanded ? "visible" : "hidden",
      textOverflow: expanded ? "clip" : "ellipsis",
      wordBreak: expanded ? "break-word" : "normal",
      overflowWrap: expanded ? "anywhere" : "normal",
      boxSizing: "border-box",
    };
  };

  const getSectionHeaderStyles = (sectionLabel, subKeys = []) => {
    if (!subKeys.length) {
      return getExpandableCellStyles(sectionLabel);
    }

    const totalWidth = subKeys.reduce((acc, sub) => {
      return (
        acc +
        (isExpanded(sectionLabel, sub)
          ? EXPANDED_COL_WIDTH
          : COLLAPSED_COL_WIDTH)
      );
    }, 0);

    const width = `${totalWidth}px`;

    return {
      minWidth: width,
      maxWidth: width,
      width,
      boxSizing: "border-box",
    };
  };

  const toggleColumnExpansion = (sectionLabel, e, subKey = "") => {
    e.stopPropagation();
    const key = getExpandKey(sectionLabel, subKey);

    setExpandedColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isScrollableColumn = useCallback(
    (label) => {
      return scrollableColumnsArray.includes(label);
    },
    [scrollableColumnsArray],
  );

  const getConfiguredSubColumns = useCallback(
    (parentLabel) =>
      (tableColumns || [])
        .filter(
          (column) =>
            isSubColumnOfParent(column, parentLabel) &&
            (column.visibility ?? true),
        )
        .sort(
          (firstColumn, secondColumn) =>
            (firstColumn.DisplayOrder ?? 0) -
            (secondColumn.DisplayOrder ?? 0),
        )
        .map((column) => getSubColumnMeta(column.label)?.childLabel)
        .filter(Boolean),
    [tableColumns],
  );

  const hasConfiguredSubColumns = useCallback(
    (parentLabel) =>
      (tableColumns || []).some((column) =>
        isSubColumnOfParent(column, parentLabel),
      ),
    [tableColumns],
  );

  const fixedColumns = useMemo(
    () =>
      tableColumns.filter(
        (column) =>
          !isSubColumn(column.label) &&
          (column.visibility ?? true) &&
          !isScrollableColumn(column.label) &&
          !(
            hasConfiguredSubColumns(column.label) &&
            getConfiguredSubColumns(column.label).length === 0
          ),
      ),
    [
      tableColumns,
      isScrollableColumn,
      hasConfiguredSubColumns,
      getConfiguredSubColumns,
    ],
  );

  const scrollableColumns = useMemo(
    () =>
      tableColumns.filter(
        (column) =>
          !isSubColumn(column.label) &&
          (column.visibility ?? true) &&
          isScrollableColumn(column.label) &&
          !(
            hasConfiguredSubColumns(column.label) &&
            getConfiguredSubColumns(column.label).length === 0
          ),
      ),
    [
      tableColumns,
      isScrollableColumn,
      hasConfiguredSubColumns,
      getConfiguredSubColumns,
    ],
  );

  const hasScrollableColumns = scrollableColumns.length > 0;
  useEffect(() => {
    setTableData(data || []);
    setIsSortingLoading(false);
  }, [data]);

  useEffect(() => {
    setTableColumns(columns || []);
  }, [columns]);

  useEffect(() => {
    if (sortingData) {
      setSortColumn(sortingData.sortBy || null);
      setSortDirection(sortingData.sortOrder || "DESC");
      setFieldSection(sortingData.fieldsection || "");
      setIsSortingLoading(false);
    }
  }, [sortingData]);

  const normalize = (str = "") =>
    str
      .toLowerCase()
      .replaceAll(/\s+/g, "")
      .replaceAll(/&amp;|&/g, "");

  const getValueByLabel = useCallback((item, label) => {
    if (!item || typeof item !== "object") {
      return "";
    }

    const normalizedLabel = normalize(label);
    const matchingKey = Object.keys(item).find(
      (key) => normalize(key) === normalizedLabel,
    );

    return matchingKey ? item[matchingKey] : "";
  }, []);

  const getEffectiveSubColumns = useCallback(
    (parentLabel, sampleRow) => {
      if (hasConfiguredSubColumns(parentLabel)) {
        return getConfiguredSubColumns(parentLabel);
      }

      const sectionValue = getValueByLabel(sampleRow, parentLabel);

      if (
        sectionValue &&
        typeof sectionValue === "object" &&
        !Array.isArray(sectionValue)
      ) {
        return Object.keys(sectionValue);
      }

      return [];
    },
    [
      hasConfiguredSubColumns,
      getConfiguredSubColumns,
      getValueByLabel,
    ],
  );

  const fixedSectionSubColumns = useMemo(() => {
    const subColumnsMap = {};

    const sampleRow =
      tableData.find(
        (row) =>
          row &&
          typeof row === "object" &&
          Object.keys(row).length > 0,
      ) || {};

    fixedColumns.forEach((column) => {
      if (column.label === fixedColumnName) {
        subColumnsMap[column.label] = getEffectiveSubColumns(
          column.label,
          sampleRow,
        );
      } else {
        subColumnsMap[column.label] = [];
      }
    });

    return subColumnsMap;
  }, [
    fixedColumns,
    tableData,
    fixedColumnName,
    getEffectiveSubColumns,
  ]);

  const sectionSubColumns = useMemo(() => {
    const subColumnsMap = {};

    const sampleRow =
      tableData.find(
        (row) =>
          row &&
          typeof row === "object" &&
          Object.keys(row).length > 0,
      ) || {};

    scrollableColumns.forEach((column) => {
      subColumnsMap[column.label] = getEffectiveSubColumns(
        column.label,
        sampleRow,
      );
    });

    return subColumnsMap;
  }, [
    scrollableColumns,
    tableData,
    getEffectiveSubColumns,
  ]);

  const handleSort = (
    colOrSubKey,
    direction,
    fieldsection = "",
    subKey = "",
  ) => {
    const isFlat = !fieldsection;

    let sortByPayload;

    if (isFlat) {
      if (typeof colOrSubKey === "string") {
        sortByPayload = colOrSubKey;
      } else {
        sortByPayload = colOrSubKey.label;
      }
    } else {
      sortByPayload = subKey;
    }

    setIsSortingLoading(true);
    setSortColumn(sortByPayload);
    setSortDirection(direction);
    setFieldSection(fieldsection);
    handleChangePage(1, sortByPayload, direction, rowsPerPage, fieldsection);
  };

  const paginatedData = useMemo(() => {
    if (paginationData?.totalRecords) return tableData;
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return tableData.slice(startIndex, endIndex);
  }, [tableData, page, rowsPerPage, paginationData]);

  const handleChangePage = (
    newPage,
    sortCol,
    sortDir,
    newRowsPerPage,
    fieldsection,
  ) => {
    setPage(newPage);

    if (handlePageChange) {
      handlePageChange(
        newPage,
        sortCol || sortColumn,
        sortDir || sortDirection,
        newRowsPerPage || rowsPerPage,
        fieldsection ?? fieldSection,
      );
    }
  };

  const handleCustomPageChange = (newPage, newPageSize) => {
    setPage(newPage);
    handleChangePage(
      newPage,
      sortColumn,
      sortDirection,
      newPageSize || rowsPerPage,
      fieldSection,
    );
  };

  const handleCustomRowsPerPageChange = (newPageSize) => {
    setRowsPerPage(newPageSize);
    setPage(1);
    handleChangePage(1, sortColumn, sortDirection, newPageSize, fieldSection);
  };

  const shouldSpanFixedHeader = tableType === "Smart Source Lens";

  const totalRecords = paginationData?.totalRecords || tableData.length;
  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  const syncElementPairHeight = (el1, el2) => {
    if (!el1 || !el2) return;

    el1.style.height = "";
    el2.style.height = "";

    const h1 = el1.getBoundingClientRect().height;
    const h2 = el2.getBoundingClientRect().height;
    const maxHeight = Math.max(h1, h2);

    el1.style.height = `${maxHeight}px`;
    el2.style.height = `${maxHeight}px`;
  };

  const syncTableHeights = useCallback(() => {
    syncElementPairHeight(fixedHeadRef.current, scrollHeadRef.current);

    const rowCount = Math.max(
      fixedRowRefs.current.length,
      scrollRowRefs.current.length,
    );

    for (let i = 0; i < rowCount; i++) {
      syncElementPairHeight(fixedRowRefs.current[i], scrollRowRefs.current[i]);
    }
  }, []);

  useLayoutEffect(() => {
    const rafId = requestAnimationFrame(() => {
      syncTableHeights();
    });

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        syncTableHeights();
      });
    });

    const elementsToObserve = [
      fixedHeadRef.current,
      scrollHeadRef.current,
      ...fixedRowRefs.current,
      ...scrollRowRefs.current,
    ].filter(Boolean);

    elementsToObserve.forEach((el) => resizeObserver.observe(el));

    window.addEventListener("resize", syncTableHeights);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", syncTableHeights);
    };
  }, [
    syncTableHeights,
    paginatedData,
    tableColumns,
    expandedColumns,
    fixedSectionSubColumns,
    sectionSubColumns,
    shouldSpanFixedHeader,
  ]);

  const getSafeValue = (value, columnLabel = "") => {
    if (value === null || value === undefined) return "-";

    if (typeof value === "object") {
      const vals = Object.values(value);
      if (vals.length) {
        return formatValue(vals[0], columnLabel);
      }
      return "-";
    }

    return formatValue(value, columnLabel);
  };

  const shouldRenderScrollableSecondHeaderRow = scrollableColumns.some(
    (col) => (sectionSubColumns[col.label] || []).length > 0,
  );

  return (
    <>
      <div
        className={styles.tableWrapper}
        style={{
          display: hasScrollableColumns ? "flex" : "block",
        }}
      >
        {/* Fixed Columns Table */}
        <TableContainer
          className={`${styles.fixedTableContainer} ${
            hasScrollableColumns ? "" : styles.fullWidthMode
          }`}
          style={{
            width: hasScrollableColumns ? "auto" : "100%",
            maxWidth: hasScrollableColumns ? "fit-content" : "100%",
          }}
        >
          <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead ref={fixedHeadRef}>
              {isInitialLoading ? (
                <HeaderShimmer columns={fixedColumns} />
              ) : (
                <>
                  {/* Header Row 1 */}
                  <TableRow>
                    {fixedColumns.map((col) => {
                      const subs = fixedSectionSubColumns[col.label] || [];
                      const hasSubs = shouldSpanFixedHeader
                        ? false
                        : subs.length > 0;

                      return (
                        <StyledTableCell
                          key={col.label}
                          colSpan={hasSubs ? subs.length : 1}
                          rowSpan={hasSubs ? 1 : 2}
                          sx={{
                            ...(hasSubs
                              ? getSectionHeaderStyles(col.label, subs)
                              : getExpandableCellStyles(col.label)),
                            cursor:
                              !hasSubs && col.sortable ? "pointer" : "default",
                          }}
                          onClick={() => {
                            if (hasSubs || !col.sortable) return;

                            const isActive =
                              (fieldSection === "" &&
                                sortColumn === col.label) ||
                              (fieldSection === col.label &&
                                sortColumn === col.label);

                            const newDirection = getSortDirection(
                              isActive,
                              sortDirection,
                            );

                            handleSort(col.label, newDirection, "", col.label);
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 6,
                              width: "100%",
                            }}
                          >
                            <span
                              style={{
                                flex: 1,
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                                overflow: "visible",
                                textOverflow: "clip",
                              }}
                            >
                              {(() => {
                                const isFlatColumn = !hasSubs;

                                if (isFlatColumn) {
                                  const isActive =
                                    (fieldSection === "" &&
                                      sortColumn === col.label) ||
                                    (fieldSection === col.label &&
                                      sortColumn === col.label);

                                  return (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 4,
                                      }}
                                    >
                                      <span>{col.label}</span>

                                      {col.sortable && (
                                        <SortIcon
                                          isActive={isActive}
                                          sortDirection={sortDirection}
                                          size={18}
                                        />
                                      )}
                                    </div>
                                  );
                                }

                                return col.label;
                              })()}
                            </span>

                            {!hasSubs && isTruncatableColumn(col.label) && (
                              <ExpandIcon
                                isExpanded={isExpanded(col.label)}
                                onClick={(e) =>
                                  toggleColumnExpansion(col.label, e)
                                }
                              />
                            )}
                          </div>
                        </StyledTableCell>
                      );
                    })}
                  </TableRow>

                  {/* Header Row 2 - Fixed sub headers */}
                  {!shouldSpanFixedHeader && (
                    <TableRow>
                      {fixedColumns.flatMap((col) => {
                        const subs = fixedSectionSubColumns[col.label] || [];
                        if (!subs.length) return [];

                        return subs.map((sub) => {
                          const isActive =
                            fieldSection === col.label && sortColumn === sub;

                          return (
                            <StyledTableCell
                              key={`${col.label}-${sub}`}
                              sx={{
                                ...getExpandableCellStyles(col.label, sub),
                                cursor: col.sortable ? "pointer" : "default",
                              }}
                              onClick={() => {
                                if (!col.sortable) return;
                                const dir = getSortDirection(
                                  isActive,
                                  sortDirection,
                                );
                                handleSort(sub, dir, col.label, sub);
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 6,
                                  width: "100%",
                                }}
                              >
                                <span
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 4,
                                    flex: 1,
                                    flexWrap: "nowrap",
                                    minWidth: 0,
                                    overflow: "visible",
                                  }}
                                >
                                  <span
                                    className={styles.headerLabel}
                                    style={{
                                      whiteSpace: "normal",
                                      wordBreak: "break-word",
                                      overflowWrap: "anywhere",
                                      overflow: "visible",
                                      textOverflow: "clip",
                                      minWidth: 0,
                                    }}
                                  >
                                    {sub}
                                  </span>

                                  {col.sortable && (
                                    <SortIcon
                                      isActive={isActive}
                                      sortDirection={sortDirection}
                                      size={16}
                                    />
                                  )}
                                </span>

                                {isTruncatableColumn(sub) && (
                                  <ExpandIcon
                                    isExpanded={isExpanded(col.label, sub)}
                                    onClick={(e) =>
                                      toggleColumnExpansion(col.label, e, sub)
                                    }
                                  />
                                )}
                              </div>
                            </StyledTableCell>
                          );
                        });
                      })}
                    </TableRow>
                  )}
                </>
              )}
            </TableHead>

            <TableBody>
              {isLoading || isSortingLoading
                ? SHIMMER_ROWS.map((rowKey) => (
                    <TableRowShimmer
                      key={`fixed-shimmer-${rowKey}`}
                      columns={fixedColumns}
                      subColumnsMap={
                        shouldSpanFixedHeader ? {} : fixedSectionSubColumns
                      }
                    />
                  ))
                : paginatedData.map((item, idx) => {
                    const firstColValue = getValueByLabel(
                      item,
                      fixedColumns?.[0]?.label,
                    );
                    const rowKey = firstColValue
                      ? `fixed-row-${firstColValue}-${idx}`
                      : `fixed-row-${idx}`;
                    return (
                      <StyledTableRow
                        key={rowKey}
                        ref={(el) => {
                          fixedRowRefs.current[idx] = el;
                        }}
                      >
                        {fixedColumns.flatMap((col) => {
                          const subs = fixedSectionSubColumns[col.label] || [];

                          if (!subs.length) {
                            const rawVal = getValueByLabel(item, col.label);
                            const val = getSafeValue(rawVal, col.label);
                            const isTotalCell =
                              idx === 0 &&
                              col.label === fixedColumns?.[0]?.label;
                            const cellContent = isTotalCell ? "TOTAL" : val;

                            return [
                              <StyledTableCell
                                key={col.label}
                                sx={getCellStyles(
                                  idx,
                                  getExpandableCellStyles(col.label),
                                )}
                              >
                                <span title={getNumberTitle(rawVal)}>
                                  {cellContent}
                                </span>
                              </StyledTableCell>,
                            ];
                          }

                          return subs.map((sub) => {
                            const sectionValue = getValueByLabel(
                              item,
                              col.label,
                            );
                            const rawSubVal = getValueByLabel(
                              sectionValue,
                              sub,
                            );
                            const cellContent =
                              idx === 0 && sub === totalColumnName
                                ? "TOTAL"
                                : getSafeValue(rawSubVal, sub);

                            return (
                              <StyledTableCell
                                key={`${col.label}-${sub}-${idx}`}
                                sx={getCellStyles(
                                  idx,
                                  getExpandableCellStyles(col.label, sub),
                                )}
                              >
                                <span>{cellContent}</span>
                              </StyledTableCell>
                            );
                          });
                        })}
                      </StyledTableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Scrollable Columns Table */}
        {hasScrollableColumns && (
          <TableContainer className={styles.scrollableTableContainer}>
            <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead ref={scrollHeadRef}>
                {isInitialLoading ? (
                  <HeaderShimmer columns={scrollableColumns} />
                ) : (
                  <>
                    {/* Header Row 1 - Section headers */}
                    <TableRow>
                      {scrollableColumns.map((col, colIdx) => {
                        const subs = sectionSubColumns[col.label] || [];
                        const hasSubs = subs.length > 0;
                        const isActive =
                          (fieldSection === "" && sortColumn === col.label) ||
                          (fieldSection === col.label &&
                            sortColumn === col.label);

                        return (
                          <StyledTableCell
                            key={col.label}
                            colSpan={hasSubs ? subs.length : 1}
                            rowSpan={hasSubs ? 1 : 2}
                            sx={{
                              textAlign: "center",
                              fontWeight: 600,
                              ...getSectionHeaderStyles(col.label, subs),
                              backgroundColor: `${getBackgroundColor(colIdx)} !important`,
                              color: `${getTextColor(colIdx)} !important`,
                              cursor:
                                !hasSubs && col.sortable
                                  ? "pointer"
                                  : "default",
                              verticalAlign: "middle",
                            }}
                            onClick={() => {
                              if (hasSubs || !col.sortable) return;

                              const newDirection = getSortDirection(
                                isActive,
                                sortDirection,
                              );

                              handleSort(
                                col.label,
                                newDirection,
                                "",
                                col.label,
                              );
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 6,
                                width: "100%",
                              }}
                            >
                              <span
                                style={{
                                  display: "block",
                                  flex: 1,
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  overflowWrap: "anywhere",
                                  overflow: "visible",
                                  textOverflow: "clip",
                                }}
                              >
                                {col.label}
                              </span>

                              {!hasSubs && col.sortable && (
                                <SortIcon
                                  isActive={isActive}
                                  sortDirection={sortDirection}
                                  size={18}
                                  color={getSortIconColor(colIdx)}
                                />
                              )}
                            </div>
                          </StyledTableCell>
                        );
                      })}
                    </TableRow>

                    {/* Header Row 2 - render ONLY real sub headers */}
                    {shouldRenderScrollableSecondHeaderRow && (
                      <TableRow>
                        {scrollableColumns.flatMap((col, colIdx) => {
                          const subs = sectionSubColumns[col.label] || [];
                          if (!subs.length) return [];

                          return subs.map((sub) => {
                            const isActive =
                              fieldSection === col.label && sortColumn === sub;

                            return (
                              <StyledTableCell
                                key={`${col.label}-${sub}`}
                                sx={{
                                  textAlign: "center",
                                  ...getExpandableCellStyles(col.label, sub),
                                  backgroundColor: `${getBackgroundColor(colIdx)} !important`,
                                  color: `${getTextColor(colIdx)} !important`,
                                  cursor: col.sortable ? "pointer" : "default",
                                }}
                                onClick={() => {
                                  if (!col.sortable) return;
                                  const newDirection = getSortDirection(
                                    isActive,
                                    sortDirection,
                                  );

                                  handleSort(sub, newDirection, col.label, sub);
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 6,
                                    width: "100%",
                                  }}
                                >
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: 4,
                                      flex: 1,
                                      flexWrap: "nowrap",
                                      minWidth: 0,
                                      overflow: "visible",
                                    }}
                                  >
                                    <span
                                      style={{
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                        overflowWrap: "anywhere",
                                        overflow: "visible",
                                        textOverflow: "clip",
                                        minWidth: 0,
                                      }}
                                    >
                                      {sub}
                                    </span>

                                    <SortIcon
                                      isActive={isActive}
                                      sortDirection={sortDirection}
                                      size={18}
                                      color={getSortIconColor(colIdx)}
                                    />
                                  </span>

                                  {isTruncatableColumn(sub) && (
                                    <ExpandIcon
                                      isExpanded={isExpanded(col.label, sub)}
                                      onClick={(e) =>
                                        toggleColumnExpansion(col.label, e, sub)
                                      }
                                    />
                                  )}
                                </div>
                              </StyledTableCell>
                            );
                          });
                        })}
                      </TableRow>
                    )}
                  </>
                )}
              </TableHead>

              <TableBody>
                {isLoading || isSortingLoading
                  ? SHIMMER_ROWS.map((rowKey) => (
                      <TableRowShimmer
                        key={`scrollable-shimmer-${rowKey}`}
                        columns={scrollableColumns}
                        subColumnsMap={sectionSubColumns}
                      />
                    ))
                  : paginatedData.map((item, idx) => {
                      const firstColValue = getValueByLabel(
                        item,
                        fixedColumns?.[0]?.label,
                      );
                      const rowKey = firstColValue
                        ? `scrollable-row-${firstColValue}-${idx}`
                        : `scrollable-row-${idx}`;
                      return (
                        <StyledTableRow
                          key={rowKey}
                          ref={(el) => {
                            scrollRowRefs.current[idx] = el;
                          }}
                        >
                          {scrollableColumns.flatMap((col) => {
                            const subs = sectionSubColumns[col.label] || [];

                            if (subs.length === 0) {
                              const rawVal = getValueByLabel(item, col.label);
                              const val = getSafeValue(rawVal, col.label);
                              const cellContent =
                                typeof rawVal === "object" ? "" : val;

                              return [
                                <StyledTableCell
                                  key={`${col.label}-__self-${idx}`}
                                  sx={getCellStyles(idx, {
                                    textAlign: "center",
                                    ...getExpandableCellStyles(col.label),
                                  })}
                                >
                                  <span title={getNumberTitle(rawVal)}>
                                    {cellContent}
                                  </span>
                                </StyledTableCell>,
                              ];
                            }

                            return subs.map((sub) => {
                              const sectionValue = getValueByLabel(
                                item,
                                col.label,
                              );
                              const rawVal = getValueByLabel(
                                sectionValue,
                                sub,
                              );
                              const valueFormatLabel =
                                isPercentageSectionColumn(
                                  col.label,
                                  PERCENTAGE_SECTION_COLUMNS,
                                )
                                  ? col.label
                                  : sub;
                              const val = getSafeValue(rawVal, valueFormatLabel);

                              return (
                                <StyledTableCell
                                  key={`${col.label}-${sub}-${idx}`}
                                  sx={getCellStyles(idx, {
                                    textAlign: "center",
                                    ...getExpandableCellStyles(col.label, sub),
                                  })}
                                >
                                  <span title={getNumberTitle(rawVal)}>
                                    {val}
                                  </span>
                                </StyledTableCell>
                              );
                            });
                          })}
                        </StyledTableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>

      <CustomPagination
        page={page}
        pageSize={rowsPerPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        onPageChange={handleCustomPageChange}
        onRowsPerPageChange={handleCustomRowsPerPageChange}
      />
    </>
  );
};

DcOverviewtable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      visibility: PropTypes.bool,
      DisplayOrder: PropTypes.number,
    }),
  ).isRequired,
  paginationData: PropTypes.shape({
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
    totalRecords: PropTypes.number,
  }),
  sortingData: PropTypes.shape({
    sortBy: PropTypes.string,
    sortOrder: PropTypes.string,
    fieldsection: PropTypes.string,
  }),
  handlePageChange: PropTypes.func,
  isLoading: PropTypes.bool,
  scrollableColumnsArray: PropTypes.arrayOf(PropTypes.string),
  fixedColumnName: PropTypes.string,
  totalColumnName: PropTypes.string,
  tableType: PropTypes.string,
  isInitialLoading: PropTypes.bool,
};

export default DcOverviewtable;
